import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ChatHeader from 'components/molecules/smsDashboard/chatHeader';
import MessagesInput from 'components/molecules/smsDashboard/messagesInput';
import MessagesList from 'components/molecules/smsDashboard/messagesList';
import ChatEmptyIcon from 'components/atoms/icons/chatEmptyIcon';
import { useSmsDashboardMessages } from 'infrastructure/hooks/smsDashboard/useMessages';
import { debounce } from 'lodash';
import { usePatientContactAvailability } from 'infrastructure/hooks/patient/use-patient-contact-availability';
import ChatScheduledHeader from 'components/molecules/smsDashboard/chatScheduledHeader';
import MessagesScheduledList from 'components/molecules/smsDashboard/messagesScheduledList';

import s from './styles.module.scss';
import { usePermissionForModifyLogic } from './use-permission-for-modify-logic';

import type { RootState } from 'store';
import type { IMessage } from 'infrastructure/interfaces';

interface IChatProps {
  scrollToMessage?: IMessage;
}

const Chat: React.FC<IChatProps> = ({ scrollToMessage }) => {
  const { t } = useTranslation();

  const { currentConversation, conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const { canModify } = usePermissionForModifyLogic();

  const isCurrentConversationExist = currentConversation.id !== 0;
  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>();
  const [debouncedSearchDateValue, setDebouncedSearchDateValue] =
    useState<string>();
  const [enabled, setEnabled] = useState(true);
  const [searchDateValue, setSearchDateValue] = useState<string>('');
  const [isSearchShown, setIsSearchShown] = useState<boolean>(false);
  const [isScheduledMessagesShown, setIsScheduledMessagesShown] =
    useState<boolean>(false);

  const updateDebouncedValue = useCallback(
    debounce((value) => setDebouncedSearchValue(value), 300),
    [],
  );
  const updateDebouncedDateValue = useCallback(
    debounce((value) => setDebouncedSearchDateValue(value), 300),
    [],
  );
  useEffect(() => {
    updateDebouncedValue(searchValue);
    return () => updateDebouncedValue.cancel();
  }, [searchValue, updateDebouncedValue]);

  const {
    messages,
    fetchNextPage,
    fetchPreviousPage,
    isRefetching,
    hasNextPage,
    hasPreviousPage,
    messagesLoading,
    fetchMessages,
  } = useSmsDashboardMessages({
    conversationId: currentConversation.id,
    searchQuery: debouncedSearchValue,
    enabled: isCurrentConversationExist && enabled,
    searchQueryDate: debouncedSearchDateValue,
  });

  const handleDateChange = async (date: any) => {
    if (date) fetchMessages();
    setSearchDateValue(date);
  };

  useEffect(() => {
    updateDebouncedDateValue(searchDateValue);
    return () => updateDebouncedDateValue.cancel();
  }, [searchDateValue, updateDebouncedDateValue]);

  const { patientGuid } = currentConversation.patient;

  const { refetchAvailabilities } = usePatientContactAvailability({
    patientGuid,
    enabled: false,
  });

  useEffect(() => {
    setIsScheduledMessagesShown(false);
  }, [conversationType, currentConversation]);

  return (
    <>
      {!isCurrentConversationExist ? (
        <div className={s['empty-conversation']}>
          <div className={s['message-wrapper']}>
            <div className={s['empty-message']}>
              <ChatEmptyIcon />
              <div className={s.message}>
                {t('smsDashboard.select_a_chat_to_start_a_conversation')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={s['chat-wrapper']}>
          {isScheduledMessagesShown ? (
            <ChatScheduledHeader
              conversation={currentConversation}
              isScheduledMessagesShown={isScheduledMessagesShown}
              setIsScheduledMessagesShown={setIsScheduledMessagesShown}
            />
          ) : (
            <ChatHeader
              canModify={canModify}
              conversation={currentConversation}
              setSearchValue={setSearchValue}
              searchValue={searchValue}
              setSearchDateValue={handleDateChange}
              isSearchShown={isSearchShown}
              setIsSearchShown={setIsSearchShown}
              setIsScheduledMessagesShown={setIsScheduledMessagesShown}
            />
          )}
          {isScheduledMessagesShown ? (
            <MessagesScheduledList
              currentConversation={currentConversation}
              isScheduledMessagesShown={isScheduledMessagesShown}
            />
          ) : (
            <MessagesList
              isRefetching={isRefetching}
              currentConversation={currentConversation}
              setEnabled={setEnabled}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              fetchNextPage={fetchNextPage}
              fetchPreviousPage={fetchPreviousPage}
              messages={messages}
              searchValue={searchValue}
              searchDateValue={searchDateValue}
              setSearchValue={setSearchValue}
              setSearchDateValue={setSearchDateValue}
              setIsSearchShown={setIsSearchShown}
              messagesLoading={messagesLoading}
              scrollToMessage={scrollToMessage}
            />
          )}
          {canModify && (
            <MessagesInput
              conversation={currentConversation}
              refetch={refetchAvailabilities}
              disabled={isScheduledMessagesShown}
              disableTemplate={isScheduledMessagesShown}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Chat;
